import { useEffect, useState } from "react";
import { api, brl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Coins, Sparkles, Loader2, Crown, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function CreditStore() {
  const { user, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(null);
  const [txs, setTxs] = useState([]);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data.packages));
    api.get("/payments/pro-plan").then((r) => setPro(r.data));
    api.get("/credits/transactions").then((r) => setTxs(r.data.transactions || [])).catch(() => {});
  }, []);

  const buy = async (pkgId) => {
    setLoading(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(null);
    }
  };

  const subscribe = async () => {
    setLoading("pro");
    try {
      const { data } = await api.post("/payments/subscribe", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(null);
    }
  };

  const cancelPro = async () => {
    if (!confirm("Deseja cancelar sua assinatura Pro?")) return;
    setLoading("cancel");
    try {
      await api.post("/payments/cancel-subscription");
      toast.success("Assinatura cancelada");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(null); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs mb-4">
            <Coins className="w-3.5 h-3.5" />
            Saldo atual: <span className="font-mono font-bold">{user?.is_pro ? "∞ (Pro)" : `${user?.credits ?? 0} créditos`}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Créditos & Planos</h1>
          <p className="mt-3 text-slate-400 text-sm">Cada pesquisa consome 1 crédito. Assinantes Pro pesquisam à vontade.</p>
        </div>

        {/* Pro Subscription */}
        {pro && (
          <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-purple-500/20 via-slate-900 to-fuchsia-500/10 border border-purple-500/40 relative overflow-hidden" data-testid="pro-plan-card">
            <div className="absolute -right-8 -top-8 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl"></div>
            <div className="relative flex flex-wrap justify-between items-start gap-6">
              <div className="flex-1 min-w-[280px]">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-200 text-[10px] font-bold uppercase tracking-widest">
                  <Crown className="w-3 h-3" /> Plano Pro
                </div>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-white">{pro.name}</h2>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-4xl font-extrabold text-white">{brl(pro.amount_cents / 100)}</span>
                  <span className="text-sm text-slate-400">/mês</span>
                </div>
                <ul className="mt-4 space-y-2">
                  {pro.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-200">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-full sm:w-auto">
                {user?.is_pro ? (
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm">
                      <Check className="w-4 h-4" /> Você é Pro
                    </div>
                    <Button onClick={cancelPro} disabled={loading === "cancel"} variant="outline" className="mt-3 w-full border-slate-700 text-slate-300 hover:bg-red-500/10 hover:text-red-300" data-testid="cancel-pro-btn">
                      <X className="w-3.5 h-3.5 mr-1.5" />Cancelar
                    </Button>
                  </div>
                ) : (
                  <Button onClick={subscribe} disabled={loading === "pro"} className="h-12 px-6 bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white font-semibold" data-testid="subscribe-pro-btn">
                    {loading === "pro" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Crown className="w-4 h-4 mr-2" />Assinar Pro</>}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Packages */}
        <h2 className="mt-12 font-display text-xl font-bold text-white">Ou compre créditos avulsos</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-5">
          {packages.map((p) => (
            <div key={p.id} className={`relative p-6 rounded-2xl border card-hover ${p.popular ? "bg-emerald-500/10 border-emerald-500/40" : "bg-slate-900/70 border-slate-800"}`} data-testid={`package-card-${p.id}`}>
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-emerald-950 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Mais popular
                </div>
              )}
              <div className="text-sm text-slate-400 uppercase tracking-widest font-mono">{p.name}</div>
              <div className="mt-3 font-display text-4xl font-extrabold text-white">
                {brl(p.amount_cents / 100)}
              </div>
              <div className="text-sm text-emerald-400 mt-1">{p.credits} créditos</div>
              <div className="mt-2 text-xs text-slate-400">≈ {brl(p.amount_cents / 100 / p.credits)} por pesquisa</div>
              <Button
                onClick={() => buy(p.id)}
                disabled={loading === p.id}
                className={`mt-6 w-full h-11 font-semibold ${p.popular ? "bg-emerald-500 hover:bg-emerald-600 text-emerald-950" : "bg-slate-800 hover:bg-slate-700 text-white"}`}
                data-testid={`buy-${p.id}-btn`}
              >
                {loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Comprar agora"}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-10 p-4 rounded-lg bg-slate-900/50 border border-slate-800 text-xs text-slate-400" data-testid="stripe-test-info">
          <strong className="text-slate-200">Modo teste Stripe:</strong> use o cartão <code className="font-mono text-emerald-400">4242 4242 4242 4242</code>, qualquer data futura e CVC.
        </div>

        {txs.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-xl font-bold text-white">Histórico</h2>
            <div className="mt-3 rounded-xl bg-slate-900/70 border border-slate-800 divide-y divide-slate-800">
              {txs.slice(0, 20).map((t) => (
                <div key={t._id} className="p-3 flex justify-between items-center text-sm" data-testid={`tx-${t._id}`}>
                  <div>
                    <div className="text-slate-200">{t.description}</div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(t.created_at).toLocaleString("pt-BR")}</div>
                  </div>
                  <div className={`font-mono font-bold ${t.amount > 0 ? "text-emerald-400" : t.amount < 0 ? "text-red-400" : "text-purple-400"}`}>
                    {t.amount > 0 ? "+" : ""}{t.amount || "PRO"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
